import { FormEvent, useState, useTransition } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import type { AuthLoginInput } from "@/types/api";

export function AuthPanel() {
  const { login } = useAuth();
  const [isPending, startTransition] = useTransition();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [loginForm, setLoginForm] = useState<AuthLoginInput>({
    email: "",
    username: "",
    password: "",
  });

  const handleLogin = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    startTransition(async () => {
      const success = await login(loginForm);
      if (!success) {
        setError("Invalid email or password. Please try again.");
        return;
      }
      // Navigation will happen automatically via PublicRoute redirect
    });
  };

  return (
    <>
      {error && (
        <div className="mb-4 rounded-md border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {error}
        </div>
      )}
      <form onSubmit={handleLogin} className="space-y-4">
        <div className="space-y-4">
          <Input
            type="text"
            value={loginForm.email || loginForm.username || ""}
            onChange={(event) =>
              setLoginForm((prev) => ({
                ...prev,
                email: event.target.value,
                username: event.target.value,
              }))
            }
            placeholder="Username or Email"
            required
            className="mb-4 px-4 py-2 bg-[#1c1f2e] text-white border border-gray-600 placeholder:text-gray-500 focus:border-blue-500"
          />
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              value={loginForm.password}
              onChange={(event) =>
                setLoginForm((prev) => ({
                  ...prev,
                  password: event.target.value,
                }))
              }
              placeholder="Password"
              required
              className="px-4 py-2 pr-10 bg-[#1c1f2e] text-white border border-gray-600 placeholder:text-gray-500 focus:border-blue-500"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>
        <div className="text-right mb-6">
          <a
            href="#"
            onClick={(e) => e.preventDefault()}
            className="text-sm text-blue-400 hover:underline"
          >
            Forgot Password?
          </a>
        </div>
        <button
          type="submit"
          disabled={isPending}
          className={`w-full py-2 bg-gradient-primary hover:bg-gradient-primary/80 text-white rounded-md font-semibold transition ${isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isPending ? 'Logging in...' : 'Continue'}
        </button>
      </form>
    </>
  );
}
